import {useMemo, useState} from "react";
import {ServiceTypeFacetEntity} from "@ots-frontend/ots-data";
import {useDebouncedValue} from "@mantine/hooks";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useTranslation} from "react-i18next";

export type UseServiceTypeFacetDataTableFilterProps = Omit<UseDataTableFilterProps<ServiceTypeFacetEntity>, 'filterConfig'>

export function useServiceTypeFacetDataTableFilter(props: UseServiceTypeFacetDataTableFilterProps): UseDataTableFilterResult<ServiceTypeFacetEntity> {
  const {records} = props;

  const {t} = useTranslation(["common"])

  const [facetQuery, setFacetQuery] = useState<string | undefined>(undefined);
  const [facetDebouncedQuery] = useDebouncedValue(facetQuery, 200);

  const [resetStatusQuery, setResetStatusQuery] = useState<string | undefined>(undefined);
  const [resetStatusDebouncedQuery] = useDebouncedValue(resetStatusQuery, 200);

  const [defaultBaselineStatusQuery, setDefaultBaselineStatusQuery] = useState<string | undefined>(undefined);
  const [defaultBaselineStatusDebouncedQuery] = useDebouncedValue(defaultBaselineStatusQuery, 200);

  const filterConfig: DataTableFilterConfig<ServiceTypeFacetEntity> = useMemo(() => {
    return {
      "facet": {
        setQuery: setFacetQuery,
        query: facetQuery,
        debouncedQuery: facetDebouncedQuery,
        filter: (record, query) => !record.facet.toLowerCase().includes(query.toLowerCase())
      },
      "resetStatus": {
        setQuery: setResetStatusQuery,
        query: resetStatusQuery,
        debouncedQuery: resetStatusDebouncedQuery,
        filter: (record, query) => !record.resetStatus.toLowerCase().includes(query.toLowerCase())
      },
      "defaultBaselineStatus": {
        setQuery: setDefaultBaselineStatusQuery,
        query: defaultBaselineStatusQuery,
        debouncedQuery: defaultBaselineStatusDebouncedQuery,
        filter: (record, query) => !record.defaultBaselineStatus.toLowerCase().includes(query.toLowerCase())
      },
    }
  }, [
    facetQuery,
    facetDebouncedQuery,
    resetStatusQuery,
    resetStatusDebouncedQuery,
    defaultBaselineStatusQuery,
    defaultBaselineStatusDebouncedQuery,
    t
  ])

  const {filteredRecords} = useDataTableFilter({
    records: records,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
